import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { InfiniteData, QueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import type { AxiosResponse } from 'axios';
import { dismissHelpable } from '@api-client/api-client';
import { helperFeedBaseKey, helperFeedCountKey, type HelperFeedPage } from '@lib/cache/helperFeed';
import type { HelpableUnseenCount } from '@models/api';
import { useToast } from './use-toast';
import { logger } from '@utils/logger';
import { sanitizeError } from '@utils/sanitizeError';

/**
 * Optimistically dismiss a helpable question from the current user's helper
 * feed. Follows the mandatory optimistic pattern, in 3 steps (no `onSuccess` —
 * the `onSettled` refetch reconciles) (.claude/skills/meetball-react-patterns/SKILL.md):
 *
 *   1. onMutate  — cancel in-flight helper-feed queries, snapshot every
 *      `['helperFeed', ...]` entry, then optimistically remove the question
 *      from every feed page and decrement the unseen-count badge.
 *   2. onError   — on a real failure (5xx / network) restore the snapshot and
 *      toast. A 404 means the match was already retired server-side (the
 *      anchor-pinned feed can outlive a match); that is effectively success, so
 *      keep the optimistic removal — no rollback, no toast.
 *   3. onSettled — invalidate the whole helper-feed family so the server
 *      reconciles both feed + count (the optimistic write only needs to be
 *      reasonable; the refetch makes it exact).
 *
 * `questionId` is passed THROUGH mutation variables (never read from a render
 * closure in the callbacks), per the stale-closure rule.
 *
 * AIDEV-NOTE: feed entries and the count entry share the `['helperFeed']` root.
 * We discriminate by data shape — a feed entry is `InfiniteData` (`.pages`),
 * the count entry is `{ unseen_count }` (no `.pages`) — so the page-walk never
 * corrupts the badge and vice-versa.
 */

type FeedCache = InfiniteData<HelperFeedPage>;

// A `['helperFeed', ...]` cache value is a feed list iff it has `.pages`.
const isFeedCache = (value: unknown): value is FeedCache =>
  value != null && Array.isArray((value as FeedCache).pages);

interface DismissContext {
  // Snapshot of every helper-feed cache entry (feed pages + count badge) for
  // rollback. Keyed/ordered exactly as getQueriesData returns them.
  snapshots: [readonly unknown[], unknown][];
}

export const useDismissHelpable = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation<AxiosResponse<void>, Error, string, DismissContext>({
    mutationFn: (questionId: string) => dismissHelpable(questionId),

    onMutate: async (questionId: string) => {
      // 1. Cancel outgoing helper-feed refetches so they don't clobber the
      //    optimistic write.
      await queryClient.cancelQueries({ queryKey: helperFeedBaseKey() });

      // 2. Snapshot the whole family for rollback BEFORE mutating.
      const snapshots = queryClient.getQueriesData({ queryKey: helperFeedBaseKey() });

      // Was the dismissed question unseen in any feed page? Only an unseen
      // removal affects the badge count.
      const wasUnseen = isQuestionUnseen(snapshots, questionId);

      // 3a. Remove the question from every FEED entry's pages (skip the count
      //     entry — discriminated by shape).
      queryClient.setQueriesData<FeedCache>({ queryKey: helperFeedBaseKey() }, old =>
        isFeedCache(old) ? removeQuestionFromFeed(old, questionId) : old,
      );

      // 3b. Decrement the badge only if the removed question was unseen.
      if (wasUnseen) {
        queryClient.setQueryData<HelpableUnseenCount>(helperFeedCountKey(), decrementCount);
      }

      return { snapshots };
    },

    onError: (error, questionId, context) => {
      // A 404 means the match was already retired server-side (the anchor-pinned
      // feed can still show a card whose match the backend dropped). The optimistic
      // removal IS the correct end-state and a retry would 404 forever, so treat it
      // as success: keep the card removed, no rollback, no toast. onSettled's
      // invalidate reconciles. Only genuine failures (5xx / network) roll back.
      const status = isAxiosError(error) ? error.response?.status : undefined;
      if (status === 404) {
        return;
      }

      logger.error('useDismissHelpable: dismiss failed', {
        error: sanitizeError(error),
        questionId,
      });

      // Rollback every snapshotted entry.
      if (context) {
        for (const [key, data] of context.snapshots) {
          queryClient.setQueryData(key, data);
        }
      }

      toast({
        title: 'Could not dismiss',
        description: 'Please try again.',
        variant: 'error',
      });
    },

    onSettled: () => {
      // Reconcile both feed + count from the server.
      void queryClient.invalidateQueries({ queryKey: helperFeedBaseKey() });
    },
  });
};

/** Floor-at-zero decrement of the unseen-count badge. */
const decrementCount = (old: HelpableUnseenCount | undefined): HelpableUnseenCount | undefined => {
  if (!old) return old;
  return { ...old, unseen_count: Math.max(0, old.unseen_count - 1) };
};

/**
 * Remove `questionId` from every page's `ids` array (and its `matches` entry,
 * since a match with no id is meaningless). New references for changed pages
 * only.
 */
const removeQuestionFromFeed = (feed: FeedCache, questionId: string): FeedCache => ({
  ...feed,
  pages: feed.pages.map(page => {
    if (!page.ids.includes(questionId)) return page;
    const { [questionId]: _removedMatch, ...restMatches } = page.matches;
    return {
      ...page,
      ids: page.ids.filter(id => id !== questionId),
      matches: restMatches,
    };
  }),
});

/**
 * True if the question appears UNSEEN (`match.is_seen === false`) in any feed
 * snapshot. Walks only feed-shaped entries; the count entry is ignored.
 */
const isQuestionUnseen = (
  snapshots: ReturnType<QueryClient['getQueriesData']>,
  questionId: string,
): boolean => {
  for (const [, data] of snapshots) {
    if (!isFeedCache(data)) continue;
    for (const page of data.pages) {
      const match = page.matches[questionId];
      if (match) return !match.is_seen;
    }
  }
  return false;
};
